import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import BaseModal from '../common/BaseModal';

const AddTransactionModal = ({
  isOpen,
  onClose,
  account,
  categoryOptions = [],
  onTransactionAdded
}) => {
  const [formData, setFormData] = useState({
    description: '',
    transaction_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    ai_category: 'other',
    category_icon: '💰'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryChange = (e) => {
    const categoryValue = e.target.value;
    const category = categoryOptions.find(c => c.value === categoryValue);
    setFormData(prev => ({
      ...prev,
      ai_category: categoryValue,
      category_icon: category?.icon || '💰'
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.description || !formData.transaction_date || !formData.total_amount) {
        throw new Error('Description, date, and amount are required');
      }

      const transactionData = {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        account_id: account.id
      };

      await onTransactionAdded(transactionData);

      // Reset form and close
      setFormData({
        description: '',
        transaction_date: new Date().toISOString().split('T')[0],
        total_amount: '',
        ai_category: 'other',
        category_icon: '💰'
      });
      onClose();
    } catch (err) {
      console.error('Error creating transaction:', err);
      setError(err.message || 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  if (!account) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={loading ? () => {} : onClose}
      size="md"
      showCloseButton={false}
      closeOnBackdrop={!loading}
    >
      <div className="flex items-center justify-between p-6 border-b">
        <h2 className="text-xl font-semibold text-gray-900">Add Transaction</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-700 text-sm">{error}</div>
          </div>
        )}

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <input
            type="text"
            id="description"
            name="description"
            required
            value={formData.description}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="e.g., Grocery shopping, Gas, Salary"
          />
        </div>

        <div>
          <label htmlFor="transaction_date" className="block text-sm font-medium text-gray-700 mb-2">
            Date *
          </label>
          <input
            type="date"
            id="transaction_date"
            name="transaction_date"
            required
            value={formData.transaction_date}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="total_amount" className="block text-sm font-medium text-gray-700 mb-2">
            Amount *
          </label>
          <input
            type="number"
            id="total_amount"
            name="total_amount"
            required
            step="0.01"
            value={formData.total_amount}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="0.00"
          />
          <p className="text-xs text-gray-500 mt-1">
            Use negative values for expenses (e.g., -50.00) and positive for income (e.g., 2000.00)
          </p>
        </div>

        <div>
          <label htmlFor="ai_category" className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{formData.category_icon}</span>
            <select
              id="ai_category"
              name="ai_category"
              value={formData.ai_category}
              onChange={handleCategoryChange}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {categoryOptions.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <p className="text-sm text-blue-800">
            <strong>Account:</strong> {account.name}
          </p>
        </div>

        <div className="flex items-center justify-end space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4 mr-2" />
            {loading ? 'Creating...' : 'Add Transaction'}
          </button>
        </div>
      </form>
    </BaseModal>
  );
};

export default AddTransactionModal;
